import axios from 'axios';
import { ENDPOINTS } from '../constants/endpoints';
import {
  Activity, ActivityCreate, BulkStatementResult, CollectionsSettings, CustomerCredit, CustomerCreditUpdate,
  DunningLevel, DunningPreviewRow, DunningRun, InvoiceSuggestion, Remittance, RemittanceCreate, RemittancePage,
  RemittancePreview, StatementSendResult, WorklistFilters, WorklistRow,
} from '../interfaces/sales/collections.interface';

const clean = (o: Record<string, unknown>) => {
  const out: Record<string, unknown> = {};
  Object.entries(o).forEach(([k, v]) => { if (v !== undefined && v !== '' && v !== null) out[k] = v; });
  return out;
};

export const CollectionsService = {
  worklist: async (institutionId: string, filters: WorklistFilters = {}): Promise<WorklistRow[]> =>
    (await axios.get(ENDPOINTS.COLLECTIONS.WORKLIST(institutionId), { params: clean({ ...filters }) })).data,
  getSettings: async (institutionId: string): Promise<CollectionsSettings> =>
    (await axios.get(ENDPOINTS.COLLECTIONS.SETTINGS(institutionId))).data,
  saveSettings: async (dto: CollectionsSettings): Promise<CollectionsSettings> =>
    (await axios.put(ENDPOINTS.COLLECTIONS.SETTINGS_SAVE, dto)).data,
  getCredit: async (customerId: string): Promise<CustomerCredit> =>
    (await axios.get(ENDPOINTS.COLLECTIONS.CREDIT(customerId))).data,
  saveCredit: async (customerId: string, dto: CustomerCreditUpdate): Promise<CustomerCredit> =>
    (await axios.put(ENDPOINTS.COLLECTIONS.CREDIT(customerId), dto)).data,
  defaultDueDate: async (customerId: string, issueDate: string): Promise<string | null> =>
    (await axios.get(ENDPOINTS.COLLECTIONS.DEFAULT_DUE_DATE(customerId, issueDate))).data?.dueDate ?? null,

  listActivity: async (customerId: string): Promise<Activity[]> =>
    (await axios.get(ENDPOINTS.COLLECTION_ACTIVITY.BY_CUSTOMER(customerId))).data,
  addActivity: async (dto: ActivityCreate): Promise<Activity> =>
    (await axios.post(ENDPOINTS.COLLECTION_ACTIVITY.BASE, dto)).data,
  deleteActivity: async (id: string): Promise<void> => {
    await axios.delete(ENDPOINTS.COLLECTION_ACTIVITY.ONE(id));
  },
};

export const RemittanceService = {
  list: async (institutionId: string, customerId?: string, page = 0, size = 20): Promise<RemittancePage> =>
    (await axios.get(ENDPOINTS.CUSTOMER_REMITTANCE.LIST(institutionId), { params: clean({ customerId, page, size }) })).data,
  get: async (id: string): Promise<Remittance> => (await axios.get(ENDPOINTS.CUSTOMER_REMITTANCE.ONE(id))).data,
  create: async (dto: RemittanceCreate): Promise<Remittance> =>
    (await axios.post(ENDPOINTS.CUSTOMER_REMITTANCE.BASE, dto)).data,
  preview: async (dto: RemittanceCreate): Promise<RemittancePreview> =>
    (await axios.post(ENDPOINTS.CUSTOMER_REMITTANCE.PREVIEW, dto)).data,
  reverse: async (id: string): Promise<Remittance> =>
    (await axios.post(ENDPOINTS.CUSTOMER_REMITTANCE.REVERSE(id))).data,
  suggest: async (institutionId: string, customerId?: string, amount?: number): Promise<InvoiceSuggestion[]> =>
    (await axios.get(ENDPOINTS.CUSTOMER_REMITTANCE.SUGGEST(institutionId), { params: clean({ customerId, amount }) })).data,
};

export const DunningService = {
  levels: async (institutionId: string): Promise<DunningLevel[]> =>
    (await axios.get(ENDPOINTS.DUNNING.LEVELS(institutionId))).data,
  saveLevel: async (dto: DunningLevel): Promise<DunningLevel> =>
    dto.id ? (await axios.put(ENDPOINTS.DUNNING.LEVEL(dto.id), dto)).data
           : (await axios.post(ENDPOINTS.DUNNING.LEVELS_BASE, dto)).data,
  deleteLevel: async (id: string): Promise<void> => {
    await axios.delete(ENDPOINTS.DUNNING.LEVEL(id));
  },
  loadDefaults: async (institutionId: string): Promise<{ created: number; skipped: number }> =>
    (await axios.post(ENDPOINTS.DUNNING.LOAD_DEFAULTS(institutionId))).data,
  preview: async (institutionId: string): Promise<DunningPreviewRow[]> =>
    (await axios.get(ENDPOINTS.DUNNING.PREVIEW(institutionId))).data,
  send: async (institutionId: string, customerIds?: string[]): Promise<DunningRun> =>
    (await axios.post(ENDPOINTS.DUNNING.SEND, { institutionId, customerIds })).data,
  history: async (institutionId: string): Promise<DunningRun[]> =>
    (await axios.get(ENDPOINTS.DUNNING.HISTORY(institutionId))).data,
};

export const StatementDeliveryService = {
  pdf: async (customerId: string, from: string, to: string): Promise<Blob> =>
    (await axios.get(ENDPOINTS.STATEMENT_DELIVERY.PDF(customerId, from, to), { responseType: 'blob' })).data,
  email: async (customerId: string, from: string, to: string): Promise<StatementSendResult> =>
    (await axios.post(ENDPOINTS.STATEMENT_DELIVERY.EMAIL(customerId), { from, to })).data,
  bulkEmail: async (institutionId: string, from: string, to: string, customerIds?: string[]): Promise<BulkStatementResult> =>
    (await axios.post(ENDPOINTS.STATEMENT_DELIVERY.BULK, { institutionId, from, to, customerIds })).data,
};
